import { CrawlRun } from '@import-io/types';
import { useQuery, useQueryClient, useMutation, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import {
  fetchSnapshot,
  fetchSnapshotComments,
  fetchSnapshotStateTransitions,
  fetchSnapshotDestinationPushes,
  fetchSnapshotHistory,
  getImportStatus,
  createSnapshotComment,
  crawlRunRequest,
  updateSnapshotLabels,
  getSnapshotList,
  fetchSnapshotChunks as fetchSnapshotChunksApi,
  fetchSnapshotChecks,
  updateSnapshotCheck,
  updateSnapshotCheckTestCase,
  resolveSnapshotChecks,
  revertSnapshotChecks,
  updateSnapshotStatus as updateSnapshotStatusApi,
  stopSnapshotRun as stopSnapshotRunApi,
  importSnapshot as importSnapshotApi,
  retrySnapshot as retrySnapshotApi,
  reextractSnapshot as reextractSnapshotApi,
  reimportSnapshot as reimportSnapshotApi,
  rerunSnapshot as rerunSnapshotApi,
  rerunTransforms as rerunTransformsApi,
  regenerateAssets as regenerateAssetsApi,
  updateSnapshot as updateSnapshotApi,
  unassignSnapshot as unassignSnapshotApi,
} from '_/api/snapshots/snapshots.api';
import zeusSdk from '_/utils/SDKs';
import { snapshotNeedsAttentionOptions } from '_/utils/snapshots';
import { getRootSnapshots, getRecentSnapshots } from '_/utils/snapshotUtils';
import { snapshotNeedsAttention } from '_/utils/timeUtils';
import { notification } from 'antd';

export const SNAPSHOT_QUERY_KEY = (snapshotId: string): string[] => ['snapshot', snapshotId];
export const SNAPSHOT_COMMENTS_QUERY_KEY = (snapshotId: string): string[] => ['snapshot', snapshotId, 'comments'];
export const SNAPSHOT_STATE_TRANSITIONS_QUERY_KEY = (snapshotId: string): string[] => [
  'snapshot',
  snapshotId,
  'stateTransitions',
];
export const SNAPSHOT_DESTINATION_PUSHES_QUERY_KEY = (snapshotId: string): string[] => [
  'snapshot',
  snapshotId,
  'destinationPushes',
];
export const SNAPSHOT_HISTORY_QUERY_KEY = (snapshotId: string): string[] => ['snapshot', snapshotId, 'history'];
export const SNAPSHOT_IMPORT_STATUS_QUERY_KEY = (snapshotId: string): string[] => ['snapshot', snapshotId, 'importStatus'];
export const SNAPSHOT_CRAWL_RUN_QUERY_KEY = (snapshotId: string): string[] => ['snapshot', snapshotId, 'crawlRun'];
export const SUPER_SNAPSHOT_QUERY_KEY = (snapshotId: string): string[] => ['snapshot', snapshotId, 'superSnapshot'];
export const SNAPSHOT_CHUNKS_QUERY_KEY = (snapshotId: string): string[] => ['snapshot', snapshotId, 'chunks'];
export const SNAPSHOT_CHECKS_QUERY_KEY = (snapshotId: string): string[] => ['snapshot', snapshotId, 'checks'];

export const useSnapshot = (snapshotId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: SNAPSHOT_QUERY_KEY(snapshotId || ''),
    queryFn: () => fetchSnapshot(snapshotId!),
    enabled: !!snapshotId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: (query) => {
      const snapshot = query.state.data;
      if (!snapshot) return false;
      const fastStatuses = ['START_PENDING', 'PENDING_QUEUE', 'START_QUEUED'];
      const slowStatuses = ['IN_PROGRESS', 'IMPORTING', 'ANALYZING'];
      if (fastStatuses.includes(snapshot.status)) return 5000;
      if (slowStatuses.includes(snapshot.status)) return 20000;
      return false;
    },
  });
};

export const useSnapshotComments = (snapshotId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: SNAPSHOT_COMMENTS_QUERY_KEY(snapshotId || ''),
    queryFn: () => fetchSnapshotComments(snapshotId!),
    enabled: !!snapshotId,
    staleTime: 30 * 1000, // 30 seconds - comments change frequently
  });
};

export const useSnapshotStateTransitions = (snapshotId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: SNAPSHOT_STATE_TRANSITIONS_QUERY_KEY(snapshotId || ''),
    queryFn: () => fetchSnapshotStateTransitions(snapshotId!),
    enabled: !!snapshotId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const useSnapshotDestinationPushes = (snapshotId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: SNAPSHOT_DESTINATION_PUSHES_QUERY_KEY(snapshotId || ''),
    queryFn: () => fetchSnapshotDestinationPushes(snapshotId!),
    enabled: !!snapshotId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const useSnapshotHistory = (snapshotId?: string, cursor?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: [...SNAPSHOT_HISTORY_QUERY_KEY(snapshotId || ''), cursor || 'initial'],
    queryFn: () => fetchSnapshotHistory(snapshotId!, cursor),
    enabled: !!snapshotId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useSnapshotImportStatus = (snapshotId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: SNAPSHOT_IMPORT_STATUS_QUERY_KEY(snapshotId || ''),
    queryFn: async () => {
      try {
        return await getImportStatus(snapshotId!);
      } catch (error: any) {
        // 404 is expected for snapshots without import status
        if (error?.status === 404 || error?.response?.status === 404) {
          return [];
        }
        throw error;
      }
    },
    enabled: !!snapshotId,
    staleTime: 10 * 1000, // 10 seconds - import status changes frequently
    refetchInterval: 10 * 1000, // Auto-refetch every 10 seconds
    retry: false, // Don't retry on 404
  });
};

export const useSnapshotCrawlRun = (snapshotId?: string): UseQueryResult<CrawlRun | null> => {
  return useQuery({
    queryKey: SNAPSHOT_CRAWL_RUN_QUERY_KEY(snapshotId || ''),
    queryFn: async () => {
      try {
        return await crawlRunRequest(snapshotId!);
      } catch (error: any) {
        console.error('Error fetching crawl run', error);
        return null;
      }
    },
    enabled: !!snapshotId,
    staleTime: 5 * 1000, // 5 seconds - crawl run status changes frequently
    refetchInterval: 10 * 1000, // Auto-refetch every 10 seconds for live status
    retry: false,
  });
};

// Helper hook to prefetch all snapshot-related data at once
const SNAPSHOTS_LIST_QUERY_KEY = ['snapshots', 'list'];
const SNAPSHOT_LIMIT = 50;

export interface SnapshotsResponse {
  items: any[];
  cursor?: string;
}

/**
 * Hook to fetch root snapshots for a source
 */
export const useSourceSnapshots = (sourceId?: string): UseQueryResult<SnapshotsResponse> => {
  return useQuery({
    queryKey: [...SNAPSHOTS_LIST_QUERY_KEY, 'source', sourceId],
    queryFn: async () => {
      if (!sourceId) return { items: [], cursor: undefined };
      const result = await getRootSnapshots(sourceId, 'sources', SNAPSHOT_LIMIT);

      // Add needsAttention property to each snapshot
      const itemsWithNeedsAttention = result.items.map((snapshot) => ({
        ...snapshot,
        needsAttention: snapshotNeedsAttention(snapshot.status, snapshot.statusUpdatedAt, snapshotNeedsAttentionOptions),
      }));

      return { items: itemsWithNeedsAttention, cursor: result.cursor };
    },
    enabled: !!sourceId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch more snapshots (pagination)
 */
export const useFetchMoreSnapshots = (sourceId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cursor: string) => {
      if (!sourceId) return { items: [], cursor: undefined };
      const result = await getRootSnapshots(sourceId, 'sources', SNAPSHOT_LIMIT, cursor);

      // Add needsAttention property to each snapshot
      const itemsWithNeedsAttention = result.items.map((snapshot) => ({
        ...snapshot,
        needsAttention: snapshotNeedsAttention(snapshot.status, snapshot.statusUpdatedAt, snapshotNeedsAttentionOptions),
      }));

      return { items: itemsWithNeedsAttention, cursor: result.cursor };
    },
    onSuccess: (newData) => {
      // Append new data to existing data
      queryClient.setQueryData<SnapshotsResponse>([...SNAPSHOTS_LIST_QUERY_KEY, 'source', sourceId], (oldData) => {
        if (!oldData) return newData;
        return {
          items: [...oldData.items, ...newData.items],
          cursor: newData.cursor,
        };
      });
    },
  });
};

/**
 * Hook to create a snapshot comment
 */
export const useCreateSnapshotComment = (snapshotId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (comment: string) => createSnapshotComment(snapshotId!, comment),
    onSuccess: () => {
      // Invalidate the comments query to refetch
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_COMMENTS_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'Comment Saved To Snapshot',
      });
    },
    onError: (error: any) => {
      console.error('Error saving comment', error);
      notification.error({
        message: error.message || 'Error saving comment',
      });
    },
  });
};

/**
 * Hook to update snapshot labels
 * Note: May show "state update on unmounted component" warning if navigating away
 * during mutation - this is harmless as React Query handles cleanup properly
 */
export const useUpdateSnapshotLabels = (snapshotId?: string): UseMutationResult<any, Error, string[]> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (labels: string[]) => updateSnapshotLabels(snapshotId!, labels),
    onSuccess: () => {
      // Invalidate the snapshot query to refetch updated labels
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'Labels Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error saving labels', error);
      notification.error({
        message: error.message || 'Error saving labels',
      });
    },
  });
};

/**
 * Hook to fetch the snapshot that superseded the current snapshot
 */
export const useSuperSnapshot = (snapshotId?: string, enabled = true): UseQueryResult<any> => {
  return useQuery({
    queryKey: SUPER_SNAPSHOT_QUERY_KEY(snapshotId || ''),
    queryFn: async () => {
      const options = { filter: `supercedes||eq||${snapshotId}`, queryParameters: { limit: 1 } };
      const items = await getSnapshotList(options);
      return items[0] || null;
    },
    enabled: !!snapshotId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch allowed user transitions for snapshots
 * Returns a map of snapshot status → allowed next statuses
 */
const ALLOWED_USER_TRANSITIONS_QUERY_KEY = ['snapshots', 'allowedUserTransitions'];

export const useAllowedUserTransitions = (): UseQueryResult<Record<string, string[]>> => {
  return useQuery({
    queryKey: ALLOWED_USER_TRANSITIONS_QUERY_KEY,
    queryFn: async () => {
      try {
        const response = await zeusSdk.snapshots.getAllowedUserTransitions();
        return response || {};
      } catch (error) {
        console.error('Error fetching allowed user transitions', error);
        return {};
      }
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - this is system configuration, rarely changes
  });
};

/**
 * Hook to reset a snapshot's import job
 */
export const useResetImportJob = (snapshotId?: string): UseMutationResult<any, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return zeusSdk.snapshots.resetImportJob(snapshotId!);
    },
    onSuccess: (response) => {
      if (response) {
        // Update the snapshot query cache with the new data
        queryClient.setQueryData(SNAPSHOT_QUERY_KEY(snapshotId!), response);
        // Also invalidate to ensure fresh data
        void queryClient.invalidateQueries({ queryKey: SNAPSHOT_QUERY_KEY(snapshotId!) });
        notification.success({
          message: 'Import job reset successfully',
        });
      }
    },
    onError: (error: any) => {
      console.error('Error resetting import job', error);
      notification.error({
        message: error.message || 'Error resetting import job',
      });
    },
  });
};

/**
 * Hook to fetch snapshot chunks (child snapshots)
 */
export interface ChunksResponse {
  items: any[];
  cursor?: string;
}

export const useSnapshotChunks = (snapshotId?: string): UseQueryResult<ChunksResponse> => {
  return useQuery({
    queryKey: SNAPSHOT_CHUNKS_QUERY_KEY(snapshotId || ''),
    queryFn: async () => {
      if (!snapshotId) return { items: [], cursor: undefined };
      return fetchSnapshotChunksApi(snapshotId, 50);
    },
    enabled: !!snapshotId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch more chunks (pagination)
 */
export const useFetchMoreChunks = (snapshotId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cursor: string) => {
      if (!snapshotId) return { items: [], cursor: undefined };
      return fetchSnapshotChunksApi(snapshotId, 50, cursor);
    },
    onSuccess: (newData) => {
      // Append new data to existing data
      queryClient.setQueryData<ChunksResponse>(SNAPSHOT_CHUNKS_QUERY_KEY(snapshotId!), (oldData) => {
        if (!oldData) return newData;
        return {
          items: [...oldData.items, ...newData.items],
          cursor: newData.cursor,
        };
      });
    },
  });
};

/**
 * Hook to refetch chunks
 */
export const useRefetchChunks = (snapshotId?: string) => {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: SNAPSHOT_CHUNKS_QUERY_KEY(snapshotId!) });
  };
};

/**
 * Hook to fetch snapshot checks
 */
export const useSnapshotChecks = (snapshotId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: SNAPSHOT_CHECKS_QUERY_KEY(snapshotId || ''),
    queryFn: () => fetchSnapshotChecks(snapshotId!),
    enabled: !!snapshotId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to update a snapshot check
 */
export const useUpdateSnapshotCheck = (
  snapshotId?: string,
): UseMutationResult<any, Error, { collectionQaTestId: string; snapshotCheck: any }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ collectionQaTestId, snapshotCheck }: { collectionQaTestId: string; snapshotCheck: any }) =>
      updateSnapshotCheck(snapshotId!, collectionQaTestId, snapshotCheck),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_CHECKS_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'Snapshot Check Updated',
      });
    },
    onError: (error: any) => {
      console.error('Error updating snapshot check', error);
      notification.error({
        message: error.message || 'Error updating snapshot check',
      });
    },
  });
};

/**
 * Hook to update a snapshot check test case
 */
export const useUpdateSnapshotCheckTestCase = (
  snapshotId?: string,
): UseMutationResult<any, Error, { testCaseId: string; snapshotCheckTestCase: any }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ testCaseId, snapshotCheckTestCase }: { testCaseId: string; snapshotCheckTestCase: any }) =>
      updateSnapshotCheckTestCase(snapshotId!, testCaseId, snapshotCheckTestCase),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_CHECKS_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'Snapshot Test Case Updated',
      });
    },
    onError: (error: any) => {
      console.error('Error updating snapshot test case', error);
      notification.error({
        message: error.message || 'Error updating snapshot test case',
      });
    },
  });
};

/**
 * Hook to resolve all snapshot checks
 */
export const useResolveSnapshotChecks = (snapshotId?: string): UseMutationResult<any, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => resolveSnapshotChecks(snapshotId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_CHECKS_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'All Snapshot Tests Resolved',
      });
    },
    onError: (error: any) => {
      console.error('Error resolving Snapshot Tests', error);
      notification.error({
        message: error.message || 'Error resolving Snapshot Tests',
      });
    },
  });
};

/**
 * Hook to revert all snapshot checks
 */
export const useRevertSnapshotChecks = (snapshotId?: string): UseMutationResult<any, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => revertSnapshotChecks(snapshotId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_CHECKS_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'All Snapshot Tests Reverted',
      });
    },
    onError: (error: any) => {
      console.error('Error reverting Snapshot Tests', error);
      notification.error({
        message: error.message || 'Error reverting Snapshot Tests',
      });
    },
  });
};

/**
 * Hook to import the latest crawl run for a source
 */
export const useImportLatestCrawlRun = (sourceId?: string): UseMutationResult<any, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => zeusSdk.sources.importLatestCrawlRun(sourceId!),
    onSuccess: () => {
      // Invalidate snapshots list to refetch
      void queryClient.invalidateQueries({
        queryKey: [...SNAPSHOTS_LIST_QUERY_KEY, 'source', sourceId],
      });
      notification.success({
        message: 'Latest Snapshot Imported',
      });
    },
    onError: (error: any) => {
      console.error('Error importing latest crawl run', error);
      if (error.status === 400) {
        notification.error({
          message: 'No New Data To Import',
        });
      } else {
        notification.error({
          message: error.messages?.[0] || 'Error importing latest crawl run',
        });
      }
    },
  });
};

/**
 * Hook to import a specific crawl run by ID for a source
 */
export const useImportCrawlRunById = (sourceId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (crawlRunId: string) => zeusSdk.sources.importCrawlRun(sourceId!, crawlRunId),
    onSuccess: () => {
      // Invalidate snapshots list to refetch
      void queryClient.invalidateQueries({
        queryKey: [...SNAPSHOTS_LIST_QUERY_KEY, 'source', sourceId],
      });
      notification.success({
        message: 'Snapshot Imported',
      });
    },
    onError: (error: any) => {
      console.error('Error importing crawl run by ID', error);
      const errorMessage = error.messages?.[0] || error.message || 'Error importing crawl run';
      notification.error({
        message: errorMessage,
      });
    },
  });
};

/**
 * Hook to update snapshot status
 */
export const useUpdateSnapshotStatus = (snapshotId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (status: string) => updateSnapshotStatusApi(snapshotId!, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'Snapshot Status Updated',
      });
    },
    onError: (error: any) => {
      console.error('Error updating snapshot status', error);
      notification.error({
        message: error.message || 'Error updating snapshot status',
      });
    },
  });
};

/**
 * Hook to stop a snapshot run
 */
export const useStopSnapshotRun = (snapshotId?: string): UseMutationResult<any, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => stopSnapshotRunApi(snapshotId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'Snapshot Run Stopped',
      });
    },
    onError: (error: any) => {
      console.error('Error stopping snapshot run', error);
      notification.error({
        message: error.message || 'Error stopping snapshot run',
      });
    },
  });
};

/**
 * Hook to import a snapshot
 */
export const useImportSnapshot = (snapshotId?: string): UseMutationResult<any, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => importSnapshotApi(snapshotId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'Snapshot Import Started',
      });
    },
    onError: (error: any) => {
      console.error('Error importing snapshot', error);
      notification.error({
        message: error.message || 'Error importing snapshot',
      });
    },
  });
};

/**
 * Hook to retry a snapshot
 */
export const useRetrySnapshot = (snapshotId?: string): UseMutationResult<any, Error, any> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (retryOptions: any) => retrySnapshotApi(snapshotId!, retryOptions),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'Snapshot Retry Started',
      });
    },
    onError: (error: any) => {
      console.error('Error retrying snapshot', error);
      notification.error({
        message: error.message || 'Error retrying snapshot',
      });
    },
  });
};

/**
 * Hook to re-extract a snapshot
 */
export const useReextractSnapshot = (snapshotId?: string): UseMutationResult<any, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => reextractSnapshotApi(snapshotId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'Snapshot Re-extract Started',
      });
    },
    onError: (error: any) => {
      console.error('Error re-extracting snapshot', error);
      notification.error({
        message: error.message || 'Error re-extracting snapshot',
      });
    },
  });
};

/**
 * Hook to re-import a snapshot
 */
export const useReimportSnapshot = (snapshotId?: string): UseMutationResult<any, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => reimportSnapshotApi(snapshotId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'Snapshot Re-import Started',
      });
    },
    onError: (error: any) => {
      console.error('Error re-importing snapshot', error);
      notification.error({
        message: error.message || 'Error re-importing snapshot',
      });
    },
  });
};

/**
 * Hook to re-run a snapshot
 */
export const useRerunSnapshot = (snapshotId?: string): UseMutationResult<any, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => rerunSnapshotApi(snapshotId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'Snapshot Re-run Started',
      });
    },
    onError: (error: any) => {
      console.error('Error re-running snapshot', error);
      notification.error({
        message: error.message || 'Error re-running snapshot',
      });
    },
  });
};

/**
 * Hook to re-run transforms on a snapshot
 */
export const useRerunTransforms = (snapshotId?: string): UseMutationResult<any, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => rerunTransformsApi(snapshotId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'Transform Re-run Started',
      });
    },
    onError: (error: any) => {
      console.error('Error re-running transforms', error);
      notification.error({
        message: error.message || 'Error re-running transforms',
      });
    },
  });
};

/**
 * Hook to regenerate assets for a snapshot
 */
export const useRegenerateAssets = (snapshotId?: string): UseMutationResult<any, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => regenerateAssetsApi(snapshotId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'Asset Regeneration Started',
      });
    },
    onError: (error: any) => {
      console.error('Error regenerating assets', error);
      notification.error({
        message: error.message || 'Error regenerating assets',
      });
    },
  });
};

/**
 * Hook to update snapshot with partial data
 */
export const useUpdateSnapshot = (snapshotId?: string): UseMutationResult<any, Error, any> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (snapshot: any) => updateSnapshotApi(snapshotId!, snapshot),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'Snapshot Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error updating snapshot', error);
      notification.error({
        message: error.message || 'Error updating snapshot',
      });
    },
  });
};

/**
 * Hook to fetch recent snapshots across all organizations and sources (super admin)
 */
const RECENT_SNAPSHOTS_QUERY_KEY = (statuses: string[], snapshotId: string) => ['snapshots', 'recent', snapshotId, ...statuses];

export const useRecentSnapshots = (statuses: string[] = [], snapshotId: string = ''): UseQueryResult<SnapshotsResponse> => {
  return useQuery({
    queryKey: RECENT_SNAPSHOTS_QUERY_KEY(statuses, snapshotId),
    queryFn: async () => {
      const result = await getRecentSnapshots(statuses, null, snapshotId);
      const itemsWithNeedsAttention = result.items.map((snapshot) => ({
        ...snapshot,
        needsAttention: snapshotNeedsAttention(snapshot.status, snapshot.statusUpdatedAt, snapshotNeedsAttentionOptions),
      }));
      return { items: itemsWithNeedsAttention, cursor: result.cursor };
    },
    staleTime: 30 * 1000,
  });
};

/**
 * Hook to fetch more recent snapshots (pagination)
 */
export const useFetchMoreRecentSnapshots = (
  statuses: string[] = [],
  snapshotId: string = '',
): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cursor: string) => {
      const result = await getRecentSnapshots(statuses, cursor, snapshotId);
      const itemsWithNeedsAttention = result.items.map((snapshot) => ({
        ...snapshot,
        needsAttention: snapshotNeedsAttention(snapshot.status, snapshot.statusUpdatedAt, snapshotNeedsAttentionOptions),
      }));
      return { items: itemsWithNeedsAttention, cursor: result.cursor };
    },
    onSuccess: (newData) => {
      queryClient.setQueryData<SnapshotsResponse>(RECENT_SNAPSHOTS_QUERY_KEY(statuses, snapshotId), (oldData) => {
        if (!oldData) return newData;
        return {
          items: [...oldData.items, ...newData.items],
          cursor: newData.cursor,
        };
      });
    },
  });
};

/**
 * Hook to bulk retry (restart) multiple snapshots
 */
export const useBulkRetrySnapshots = (
  onSuccess?: () => void,
): UseMutationResult<any, Error, { id: string; orgSlug: string }[]> => {
  return useMutation({
    mutationFn: async (snapshots: { id: string; orgSlug: string }[]) => {
      const retryOptions = { options: { skipQaTests: false, rerunQueries: true } };
      const results = await Promise.allSettled(
        snapshots.map(({ id, orgSlug }) => zeusSdk.snapshots.retrySnapshot(id, retryOptions, { orgSlug: orgSlug })),
      );
      const failedCount = results.filter((r) => r.status === 'rejected').length;
      if (failedCount === results.length) {
        throw new Error('All snapshots failed to restart');
      }
      return results;
    },
    onSuccess: (results) => {
      const succeededCount = results.filter((r: any) => r.status === 'fulfilled').length;
      notification.success({ message: `${succeededCount} snapshot(s) restarted` });
      onSuccess?.();
    },
    onError: (error: any) => {
      notification.error({ message: error.message || 'Failed to restart snapshots' });
    },
  });
};

/**
 * Hook to bulk cancel multiple snapshots
 */
export const useBulkCancelSnapshots = (
  onSuccess?: () => void,
): UseMutationResult<any, Error, { id: string; orgSlug: string }[]> => {
  return useMutation({
    mutationFn: async (snapshots: { id: string; orgSlug: string }[]) => {
      const results = await Promise.allSettled(
        snapshots.map(({ id, orgSlug }) => zeusSdk.snapshots.stopSnapshotRun(id, { orgSlug: orgSlug })),
      );
      const failedCount = results.filter((r) => r.status === 'rejected').length;
      if (failedCount === results.length) {
        throw new Error('All snapshots failed to cancel');
      }
      return results;
    },
    onSuccess: (results) => {
      const succeededCount = results.filter((r: any) => r.status === 'fulfilled').length;
      notification.success({ message: `${succeededCount} snapshot(s) cancelled` });
      onSuccess?.();
    },
    onError: (error: any) => {
      notification.error({ message: error.message || 'Failed to cancel snapshots' });
    },
  });
};

/**
 * Hook to unassign snapshot from user
 */
export const useUnassignSnapshot = (snapshotId?: string): UseMutationResult<any, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => unassignSnapshotApi(snapshotId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_QUERY_KEY(snapshotId!) });
      notification.success({
        message: 'Snapshot Unassigned',
      });
    },
    onError: (error: any) => {
      console.error('Error unassigning snapshot', error);
      notification.error({
        message: error.message || 'Error unassigning snapshot',
      });
    },
  });
};
